import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { bankOrder } from "@/utils/data"
import { turnTotalTime } from "@mono/constants"
import { gameBackgrounds, getStoredBackgroundId } from "@/utils/gameBackgrounds"
import {
  applyPiecePreference,
  applyPiecePreferenceToGame,
  getCachedPreferredPieceId,
} from "@/utils/piecePreference"
import { getUserCreds } from "@/utils/utils"

const storedBackground =
  gameBackgrounds.find((bg) => bg.id === getStoredBackgroundId()) ||
  gameBackgrounds[0]

// Every game modal shut. Three reducers need a clean slate — a game starting,
// "close everything", and walking out of a game — and a modal added to one
// copy of this list but not the others is a modal that survives a reset, so
// they all read it from here. A factory, not a constant: each caller gets its
// own object to hand to immer.
const allModalsClosed = (): IModalData => ({
  auction: { visible: false },
  auctionResult: { visible: false },
  buyOrAuction: { visible: false },
  decisionHub: { visible: false },
  doubleRent: { visible: false },
  forcedBuy: { visible: false },
  fullStats: { visible: false },
  hiddenAuction: { visible: false },
  jail: { showOptionForUseJailFreeCard: false, visible: false },
  jailFreeCard: { visible: false, type: "chance" },
  playerCards: undefined,
  propMng: { visible: false },
  bankrupt: { visible: false },
  endGame: { visible: false },
  gameEndWithoutStats: { visible: false },
  stockExchange: { visible: false },
  sayNo: { visible: false },
  trade: { visible: false },
  chance: undefined,
})

const emptyAnimationData = (): IAnimationData => ({
  dice: false,
  propChangesAppliedAnimation: {
    houseAnimations: [],
    mortgageAnimations: [],
  },
  goToJailAnimations: [],
  propertyMoveAnimations: [],
  glowingProperties: [],
  tradeGlowProperties: [],
})

const initialState: IGlobalState = {
  isMobile: window.innerWidth < 1000,
  isPortrait: window.matchMedia("(orientation: portrait)").matches,
  notifications: [],
  toastNotifications: [],
  isLoggedIn: false,
  forgetPasswordEmail: "",
  gameList: [],
  myGameId: "",
  idleWarning: null,
  cheats: localStorage.getItem("cheats") === "true",
  animationData: emptyAnimationData(),
  actionCardAnimationRunning: 0,
  showHistory: false,
  tempCount: 0,
  timerValue: turnTotalTime,
  isTimerPaused: true,
  diceRolling: false,
  disableEndTurnLocally: true,
  modalData: {
    jail: { showOptionForUseJailFreeCard: false, visible: false },
    jailFreeCard: { visible: false, type: "chance" },
  },
  elements: { playerListItems: [], chanceCards: [], communityChestCards: [] },
  tradeData: { status: "createing-offer" },
  tempPropMngData: { houseData: [], mortgagedProperties: [] },
  houseBuildMode: null,
  viewedTradeId: null,
  chatMessages: {},
  chatClouds: {},
  botTakeoverCountdowns: {},
  selectedTheme: {
    id: storedBackground.id,
    name: storedBackground.name,
    image: storedBackground.id,
  },
  // Last known value, replaced by the account's own as soon as the user data
  // request in App.tsx comes back.
  preferredPieceId: getCachedPreferredPieceId(getUserCreds().email),
  moneyAnimations: [],
  lastDeclinedTrades: {},
  playerCounts: { online: 0, inGame: 0 },
  quickJoinQueueCount: 0,
  pendingDecision: null,
  gameEndType: null,
  boardPickerMode: null,
  helpTourActive: false,
}

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    // Game data reaches the store through this reducer and setGameSituation
    // below, and nothing is ever sent back up from here, so those two are the
    // right (and only) place to swap the viewer onto their preferred piece. Do
    // it here and every screen reading `myGame` shows the right token without
    // knowing the feature exists.
    setState: (state, action: PayloadAction<Partial<IGlobalState>>) => {
      const next = { ...state, ...action.payload }
      if (action.payload.myGame) {
        next.myGame = applyPiecePreferenceToGame(
          next.myGame,
          next.preferredPieceId,
          getUserCreds().email
        )
      }
      // Entering house build/sell mode claims board clicks the same way a
      // board pick does — only one of them should be active at a time.
      if (action.payload.houseBuildMode) {
        next.boardPickerMode = null
      }
      return next
    },
    setGameSituation: (
      state,
      action: PayloadAction<Partial<IGameSituation>>
    ) => {
      if (state.myGame) {
        state.myGame.situation = {
          ...state.myGame.situation,
          ...action.payload,
        }
        if (action.payload.players && state.myGame.situation) {
          state.myGame.situation.players = applyPiecePreference(
            state.myGame.situation.players,
            state.preferredPieceId,
            getUserCreds().email
          ) as IPlayer[]
        }
      }
    },
    // Settings can change while a game is running — the table can vote itself
    // onto fast pace — and they arrive on their own socket event, because the
    // situation broadcast carries no settings at all.
    setGameSettings: (state, action: PayloadAction<Partial<IGameSettings>>) => {
      if (state.myGame) {
        state.myGame.settings = { ...state.myGame.settings, ...action.payload }
      }
    },
    // Switching pieces mid-game re-runs the swap on the game already in the
    // store, so the board and the player cards change over immediately instead
    // of waiting for the next update from the server.
    setPreferredPieceId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.preferredPieceId = action.payload
      if (state.myGame?.situation?.players) {
        state.myGame.situation.players = applyPiecePreference(
          state.myGame.situation.players,
          action.payload,
          getUserCreds().email
        ) as IPlayer[]
      }
    },
    setPlayerListElements: (
      state,
      action: PayloadAction<IPlayerListItemElementObject[]>
    ) => {
      state.elements.playerListItems = action.payload
    },
    setGlobalElement: (
      state,
      action: PayloadAction<Partial<IGlobalElementsObject>>
    ) => {
      state.elements = { ...state.elements, ...action.payload }
    },
    setModalData: (state, action: PayloadAction<Partial<IModalData>>) => {
      state.modalData = { ...state.modalData, ...action.payload }
      // Re-opening the player cards modal (or the trade/property-management
      // modals) while a board pick is in progress would leave two features
      // both claiming board clicks, so any of them opening cancels the pick.
      if (
        action.payload.playerCards?.visible ||
        action.payload.trade?.visible ||
        action.payload.propMng?.visible
      ) {
        state.boardPickerMode = null
      }
    },
    // Entering board-picker mode for a board-targeted card has to clear out
    // every other UI that also claims clicks on the board or overlaps it —
    // otherwise a cell click could get read by two features at once, or the
    // picker banner would sit behind another modal. Passing null just
    // cancels/finishes the pick.
    setBoardPickerMode: (
      state,
      action: PayloadAction<IGlobalState["boardPickerMode"]>
    ) => {
      state.boardPickerMode = action.payload
      if (action.payload) {
        state.modalData = {
          ...state.modalData,
          trade: { visible: false },
          propMng: { visible: false },
          playerCards: { visible: false, playerOrder: action.payload.playerOrder },
        }
        state.houseBuildMode = null
      }
    },
    // Second step of the swap-position pick, for the case where the tile that
    // was clicked holds several opponents: it parks the tile so the banner can
    // ask which of them was meant. null steps back out to picking a tile.
    setBoardPickerPendingCell: (
      state,
      action: PayloadAction<number | null>
    ) => {
      if (!state.boardPickerMode) return
      state.boardPickerMode.pendingCellOrder = action.payload
    },
    setAnimationData: (
      state,
      action: PayloadAction<Partial<IAnimationData>>
    ) => {
      state.animationData = { ...state.animationData, ...action.payload }
    },
    addGlowingProperties: (state, action: PayloadAction<number[]>) => {
      const current = state.animationData.glowingProperties ?? []
      state.animationData.glowingProperties = [
        ...new Set([...current, ...action.payload]),
      ]
    },
    removeGlowingProperties: (state, action: PayloadAction<number[]>) => {
      const toRemove = new Set(action.payload)
      state.animationData.glowingProperties = (
        state.animationData.glowingProperties ?? []
      ).filter((order) => !toRemove.has(order))
    },
    addTradeGlowProperties: (state, action: PayloadAction<number[]>) => {
      const current = state.animationData.tradeGlowProperties ?? []
      state.animationData.tradeGlowProperties = [
        ...new Set([...current, ...action.payload]),
      ]
    },
    removeTradeGlowProperties: (state, action: PayloadAction<number[]>) => {
      const toRemove = new Set(action.payload)
      state.animationData.tradeGlowProperties = (
        state.animationData.tradeGlowProperties ?? []
      ).filter((order) => !toRemove.has(order))
    },
    setTradeData: (state, action: PayloadAction<Partial<ITradeData>>) => {
      state.tradeData = { ...state.tradeData, ...action.payload }
    },
    resetTradeData: (state) => {
      state.tradeData = { status: "createing-offer", activeTrade: undefined }
    },
    // Remembered per opponent, so offering to A then to B and having both
    // declined restores the right draft for whichever one is picked next.
    rememberDeclinedTrade: (state, action: PayloadAction<ITrade>) => {
      state.lastDeclinedTrades[action.payload.offeredPlayerOrder] =
        action.payload
    },
    forgetDeclinedTrade: (state, action: PayloadAction<PlayerOrder>) => {
      delete state.lastDeclinedTrades[action.payload]
    },
    setActiveTrade: (state, action: PayloadAction<Partial<ITrade>>) => {
      if (state.tradeData.activeTrade) {
        state.tradeData.activeTrade = {
          ...state.tradeData.activeTrade,
          ...action.payload,
        }
      } else {
        state.tradeData.activeTrade = action.payload as ITrade
      }
    },
    resetStateOnGameStart: (state) => {
      state.notifications = []
      state.modal = undefined
      state.diceRolling = false
      state.disableEndTurnLocally = false
      state.idleWarning = null
      state.botTakeoverCountdowns = {}
      // Both of these gate the bankrupt modal in App.tsx and live only in the
      // store, so a game started in the same tab inherits whatever the last one
      // left behind: a player who declared bankruptcy last game would never see
      // the modal again, and a trade chooser open when the game ended would keep
      // suppressing it.
      state.declaredBankruptcy = false
      state.tempTradeData = { status: undefined } as any
      state.modalData = allModalsClosed()
      state.moneyAnimations = []
      state.houseBuildMode = null
      state.viewedTradeId = null
      state.lastDeclinedTrades = {}
      state.pendingDecision = null
      state.gameEndType = null
      state.boardPickerMode = null
    },
    // Walking out through the leave button. The server takes this socket out of
    // the game's room at the same moment, so nothing more arrives for it — this
    // is the other half: everything the game left behind in the store goes too.
    //
    // It has to be this thorough because the parts of the app that draw a game
    // are not confined to the game route. The socket handlers, the toast
    // container and the end-game modal all live in App, above <Routes>, and
    // most of them decide whether to act by looking at `myGame`. Clearing it
    // (and the notifications, sounds and modals already queued behind it) is
    // what makes leaving actually mean leaving.
    leftGame: (state, action: PayloadAction<string>) => {
      state.myGame = undefined
      state.myGameId = ""
      state.notifications = []
      // Game invites are the one toast that outlives the game: they belong to
      // the lobby, and they sit for minutes on purpose, so an invitation that
      // arrived mid-game is still waiting when its recipient walks out.
      state.toastNotifications = state.toastNotifications.filter(
        (toast) => toast.type === "game-invite"
      )
      state.moneyAnimations = []
      state.animationData = emptyAnimationData()
      state.modalData = allModalsClosed()
      state.modal = undefined
      state.tradeData = { status: "createing-offer", activeTrade: undefined }
      state.tempTradeData = { status: undefined } as any
      state.lastDeclinedTrades = {}
      state.viewedTradeId = null
      state.houseBuildMode = null
      state.tempPropMngData = { houseData: [], mortgagedProperties: [] }
      state.declaredBankruptcy = false
      state.disableEndTurnLocally = true
      state.diceRolling = false
      state.pieceMovementAnimationRunning = false
      state.actionCardAnimationRunning = 0
      state.showAllAuctionBids = false
      state.auctionBids = undefined
      state.hoveredItem = undefined
      state.forceDowngradeHighlightOrder = null
      state.idleWarning = null
      state.botTakeoverCountdowns = {}
      state.pendingDecision = null
      state.gameEndType = null
      state.boardPickerMode = null
      state.isGamePaused = false
      state.isTimerPaused = true
      state.timerValue = turnTotalTime
      state.showHistory = false
      // Declared as a full Record but used as a sparse one everywhere (see
      // hideChatCloud, which deletes keys), so an empty map needs the cast.
      state.chatClouds = {} as typeof state.chatClouds
      delete state.chatMessages[action.payload]
    },
    addNotification: (
      state,
      action: PayloadAction<INotification & { removeAll?: boolean }>
    ) => {
      if (action.payload.removeAll) {
        state.notifications = [action.payload]
      } else {
        state.notifications.push(action.payload)
      }
    },
    removeNotification: (
      state,
      action: PayloadAction<INotification["type"]>
    ) => {
      state.notifications = state.notifications.filter(
        (n) => n.type !== action.payload
      )
    },
    addToastNotification: (
      state,
      action: PayloadAction<Partial<IToastNotification>>
    ) => {
      const newToast: IToastNotification = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        createdAt: Date.now(),
      } as IToastNotification
      state.toastNotifications.push(newToast)
    },
    removeToastNotification: (state, action: PayloadAction<string>) => {
      state.toastNotifications = state.toastNotifications.filter(
        (t) => t.id !== action.payload
      )
    },
    setTempPropMngData: (
      state,
      action: PayloadAction<Partial<IGlobalState["tempPropMngData"]>>
    ) => {
      state.tempPropMngData = { ...state.tempPropMngData, ...action.payload }
    },
    addHouseToProperty: (state, action: PayloadAction<number>) => {
      const propertyOrder = action.payload
      state.tempPropMngData.houseData = state.tempPropMngData.houseData.map(
        (item) =>
          item.propertyOrder === propertyOrder
            ? { propertyOrder, houses: item.houses + 1 }
            : item
      )
    },
    removeHouseFromProperty: (state, action: PayloadAction<number>) => {
      const propertyOrder = action.payload
      state.tempPropMngData.houseData = state.tempPropMngData.houseData.map(
        (item) =>
          item.propertyOrder === propertyOrder
            ? { ...item, houses: item.houses - 1 }
            : item
      )
    },
    incementActionCardAnimationRunning: (state) => {
      state.actionCardAnimationRunning += 1
    },
    decrementActionCardAnimationRunning: (state) => {
      state.actionCardAnimationRunning -= 1
    },
    setOrientation: (state, action: PayloadAction<boolean>) => {
      state.isPortrait = action.payload
    },
    setIsMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload
    },
    addChatMessage: (
      state,
      action: PayloadAction<{ roomid: string; message: IChatMessage }>
    ) => {
      const { roomid, message } = action.payload
      if (!state.chatMessages[roomid]) {
        state.chatMessages[roomid] = []
      }
      state.chatMessages[roomid].push(message)
    },
    clearChatMessages: (state, action: PayloadAction<{ roomid: string }>) => {
      const { roomid } = action.payload
      delete state.chatMessages[roomid]
    },
    showChatCloud: (
      state,
      action: PayloadAction<{
        playerOrder: PlayerOrder
        message: string
        username: string
      }>
    ) => {
      const { playerOrder, message, username } = action.payload
      state.chatClouds[playerOrder] = { message, username, isVisible: true }
    },
    hideChatCloud: (
      state,
      action: PayloadAction<{ playerOrder: PlayerOrder }>
    ) => {
      const { playerOrder } = action.payload
      delete state.chatClouds[playerOrder]
    },
    resetAllModals: (state) => {
      state.modal = undefined
      state.modalData = allModalsClosed()
      state.houseBuildMode = null
      state.viewedTradeId = null
      state.boardPickerMode = null
    },
    // On our own turn this comes from useIdleAutoAction; on everyone else's it
    // comes from the relayed "game.idle-warning". null clears the badge.
    setIdleWarning: (state, action: PayloadAction<IIdleWarning | null>) => {
      state.idleWarning = action.payload
    },
    // The server owns this countdown end to end (it is the one holding the
    // seat), so the client only ever mirrors what it is told. null means the
    // seat is no longer on the clock — reconnected, or already taken over.
    setBotTakeoverCountdown: (
      state,
      action: PayloadAction<{
        playerOrder: PlayerOrder
        secondsLeft: number | null
      }>
    ) => {
      const { playerOrder, secondsLeft } = action.payload
      if (secondsLeft === null) {
        delete state.botTakeoverCountdowns[playerOrder]
      } else {
        state.botTakeoverCountdowns[playerOrder] = secondsLeft
      }
    },
    addMoneyAnimation: (
      state,
      action: PayloadAction<IMoneyTransferAnimation>
    ) => {
      if (!state.moneyAnimations) {
        state.moneyAnimations = []
      }
      state.moneyAnimations.push(action.payload)
    },
    addMoneyAnimations: (
      state,
      action: PayloadAction<IMoneyTransferAnimation[]>
    ) => {
      if (!state.moneyAnimations) {
        state.moneyAnimations = []
      }
      state.moneyAnimations.push(...action.payload)
    },
    removeMoneyAnimation: (state, action: PayloadAction<string>) => {
      if (state.moneyAnimations) {
        state.moneyAnimations = state.moneyAnimations.filter(
          (anim: IMoneyTransferAnimation) => anim.id !== action.payload
        )
      }
    },
    setMoneyAnimationAsStarted: (state, action: PayloadAction<string>) => {
      if (state.moneyAnimations) {
        const animation = state.moneyAnimations.find(
          (anim: IMoneyTransferAnimation) => anim.id === action.payload
        )
        if (animation) {
          animation.started = true
        }
      }
    },
    setMoneyAnimations: (
      state,
      action: PayloadAction<IMoneyTransferAnimation[]>
    ) => {
      state.moneyAnimations = action.payload
    },
  },
})

export const {
  setState,
  setGameSituation,
  setGameSettings,
  setPreferredPieceId,
  setPlayerListElements,
  setGlobalElement,
  setModalData,
  setBoardPickerMode,
  setBoardPickerPendingCell,
  setAnimationData,
  addGlowingProperties,
  removeGlowingProperties,
  addTradeGlowProperties,
  removeTradeGlowProperties,
  setTradeData,
  setActiveTrade,
  resetTradeData,
  rememberDeclinedTrade,
  forgetDeclinedTrade,
  addNotification,
  removeNotification,
  addToastNotification,
  removeToastNotification,
  setTempPropMngData,
  addHouseToProperty,
  removeHouseFromProperty,
  incementActionCardAnimationRunning,
  decrementActionCardAnimationRunning,
  setOrientation,
  setIsMobile,
  addChatMessage,
  clearChatMessages,
  showChatCloud,
  hideChatCloud,
  resetAllModals,
  setIdleWarning,
  setBotTakeoverCountdown,
  addMoneyAnimation,
  addMoneyAnimations,
  removeMoneyAnimation,
  setMoneyAnimationAsStarted,
  setMoneyAnimations,
  resetStateOnGameStart,
  leftGame,
} = globalSlice.actions

export default globalSlice.reducer
